import { useOrderStore } from "@smeetz/order-store";
import { OrderToken, TokenStatus } from "@smeetz/order-common";
import UserModule from "@/store/modules/user";
import { saveOrderForSync } from "@/db/orders";
import { scanByToken } from "@/utils/vue-helpers/orders.helper";
import { LogLevel, LogService } from "@/services/logging";

/**
 * Order-flow ticket scanning.
 *
 * Each scannable token is scanned locally (offline-safe) via the order package —
 * `scanByToken` sets `scannedAt`/`scannedBy` without a network call — then the
 * order is re-queued through the offline-durable orders queue. `persistOrder`
 * sends the full `tokens` array, and the backend syncs the scans; no dedicated
 * scan call is needed from the POS.
 */

/** Tokens eligible to be scanned: ACTIVE (not cancelled) and not already scanned. */
function isScannable(token: OrderToken): boolean {
  return token.status === TokenStatus.ACTIVE && !token.scannedAt;
}

/**
 * Bulk-scan all of the selected order's still-scannable tokens (the order-flow
 * equivalent of the old `scanTickets` "mark all confirmed unscanned tickets").
 *
 * This is the only ticket-scan path in checkout, matching prod (which had bulk scan +
 * Teams NFC only — no per-ticket barcode scan in the checkout screen).
 */
export async function scanOrderTicketsBulk(): Promise<void> {
  const order = useOrderStore().selectedOrder;
  if (!order) return;

  const tokens: OrderToken[] = order.tokens ?? [];
  let scannedAny = false;
  for (const token of tokens) {
    if (!isScannable(token)) continue;
    try {
      await scanByToken(token.token, UserModule.userId ?? undefined);
      scannedAny = true;
    } catch (error) {
      LogService.info(
        `Scan: failed to scan token ${token.id}`,
        { error },
        LogLevel.Error
      );
    }
  }

  if (scannedAny) {
    // Re-queue the order (scanned tokens included); the backend syncs the scans on persist.
    await saveOrderForSync(useOrderStore().selectedOrder);
  }
}
